import * as React from 'react';
import type { StageErrorSummary } from '@chess/api-insights';
import { FilterAnchor, StatTile, TextSkeleton, type CardProps, type FilterLink } from '@chess/api-insights/charts';
import { CardAside, CardEmpty, CardNote } from '@chess/api-dashboard/react';
import './middlegame-misses.css';

// The middlegame misses card: chances the reader did not take in the
// middlegame, such as an opponent's blunder left unpunished, over the last
// 30 days, and the way to those games in the library. Drawn from
// props only; the container reads the game store. The card's body: the
// section draws it in the dashboard's frame (`MIDDLEGAME_MISSES_CARD`, `registerCard`).

export type MiddlegameMissesCardProps = CardProps<{ middlegame: StageErrorSummary }, {
  /** Opens the library filtered to games with a middlegame miss. */
  link?: FilterLink;
}>;

/** The frame's title: the registration's and the stories'. */
export const MIDDLEGAME_MISSES_CARD = { title: 'Middlegame misses' };
const ASIDE = 'chances not taken';

export const MiddlegameMissesCard: React.FC<MiddlegameMissesCardProps> = (props) => {
  if (props.loading)
    return (
      <>
        <CardAside>{ASIDE}</CardAside>
        <StatTile loading unit="misses" />
        <p className="middlegame-card-ratio">
          <TextSkeleton sample="In 3 of 7 games · 0.6 per game" />
        </p>
        <CardNote>
          <TextSkeleton sample="7 scanned games with a middlegame" />
        </CardNote>
      </>
    );
  const { middlegame, link } = props;
  const current = middlegame;
  const note = current.scanned ? `${current.scanned} scanned ${current.scanned === 1 ? 'game' : 'games'} with a middlegame` : undefined;
  let body: React.ReactNode;
  if (!current.scanned) body = <CardEmpty>Scan games to find the chances you missed.</CardEmpty>;
  else if (!current.misses) body = <CardEmpty>No missed chances in the middlegame in the last 30 days.</CardEmpty>;
  else
    body = (
      <>
        <StatTile value={current.misses} unit={current.misses === 1 ? 'miss' : 'misses'} />
        <p className="middlegame-card-ratio">
          In {current.missGames} of {current.scanned} {current.scanned === 1 ? 'game' : 'games'} · {(current.misses / current.scanned).toFixed(1)} per game
        </p>
        <FilterAnchor className="middlegame-card-link" link={link} filter={{ blunder: 'middlegame-miss' }} title="Every game with a middlegame miss, of the last 30 days">
          Games in the library →
        </FilterAnchor>
      </>
    );
  return (
    <>
      <CardAside>{ASIDE}</CardAside>
      {body}
      <CardNote>{note}</CardNote>
    </>
  );
};
